import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Linkedin, Globe } from "lucide-react";
import Logo from "../components/Logo";
import { useLang } from "../context/LangContext";

// ─────────────────────────────────────────────────────────────────────
// Founders page, accessible from the "Founders" nav link.
// Order: David Provenzani → Ali Mecirdi → Jallel Bendjaballah → Ade Oladeinde.
// Bilingual (EN / AR) with RTL mirroring, driven by LangContext.
// ─────────────────────────────────────────────────────────────────────

const FOUNDERS = [
  {
    id: "david",
    photo: "/founders/david.jpg",
    linkedin: "https://www.linkedin.com/in/davidprovenzani",
    accent: "#01AF9B",
    previewScale: 1,
    previewTranslateY: 0,
    en: {
      name: "David Provenzani",
      role: "Founder & CEO",
      location: "United Arab Emirates",
      quote: "Every company, regardless of size, deserves access to professional ESG reporting, without a prohibitive cost.",
      overview: [
        "David Provenzani is an Italian-born green architect, ecopreneur and sustainability strategist based in Dubai, United Arab Emirates, with more than twenty-five years of executive leadership spanning renewable energy, green building design and infrastructure. He founded ESG Report Builder from the conviction that professional-grade sustainability reporting should be within reach of every business, not only large corporations.",
        "As Founder and Managing Director of Architaly Green Energy DMCC in Dubai, and Founder of Architaly Green Design in Italy, he has spent his career at the intersection of architecture, clean energy and education, translating sustainability principles into practical commercial and civic programmes across the Gulf region. His portfolio spans commercial developments, industrial facilities and civic projects where energy performance, embodied carbon and human-centred design are treated as inseparable disciplines rather than optional add-ons.",
        "Beyond his commercial practice, David has invested consistently in education for sustainability. He created the Dubai Solar Schools Programme along with the affiliated Green Team and Solar Mobility initiatives now adopted across schools in the U.A.E., serves as Honorary Professor at Amity School of Architecture and Planning in Dubai, and sits on the leadership team of the Singularity University Dubai Chapter, where he mentors founders and students on the practical intersection of technology, urban design and the low-carbon economy.",
      ],
      highlights_title: "Career Highlights",
      highlights: [
        "Founder & Managing Director, Architaly Green Energy DMCC, Dubai, U.A.E.",
        "Founder, Architaly Green Design, Italy",
        "Founder, ESG Report Builder",
        "Creator of the Dubai Solar Schools Programme and the affiliated Green Team and Solar Mobility initiatives adopted across schools in the U.A.E.",
        "Leadership Team Member, Singularity University Dubai Chapter",
      ],
      recognition_title: "Recognition & Education",
      recognition: [
        "Honorary Professor, Amity School of Architecture and Planning, Amity University Dubai",
        "Recognised at the Young Global Leaders Summit, Ras Al Khaimah, for contributions to education for sustainability",
        "Regularly engaged as a futurist and careers mentor on the role of technology and sustainability, David has been named among the Top 50 AI Personalities in the Gulf Region by Deep Knowledge Analytics",
      ],
      tags: ["Green Architecture", "Renewable Energy", "Sustainability Education", "ESG Strategy"],
    },
    ar: {
      name: "ديفيد بروفنزاني",
      role: "المؤسس والرئيس التنفيذي",
      location: "الإمارات العربية المتحدة",
      quote: "كل شركة، مهما كان حجمها، تستحق الوصول إلى تقارير الاستدامة الاحترافية، دون تكلفة باهظة.",
      overview: [
        "ديفيد بروفنزاني مهندس معماري أخضر من أصل إيطالي، ورائد أعمال في مجال البيئة، ومستشار استراتيجي للاستدامة يقيم في دبي بالإمارات العربية المتحدة، ويتمتع بأكثر من خمسة وعشرين عامًا من الخبرة القيادية التنفيذية في مجالات الطاقة المتجددة، وتصميم المباني الخضراء، والبنية التحتية. أسّس منصة ESG Report Builder انطلاقًا من قناعته بأن التقارير الاحترافية للاستدامة يجب أن تكون في متناول كل شركة، وليس فقط الشركات الكبرى.",
        "بصفته مؤسسًا ومديرًا تنفيذيًا لشركة Architaly Green Energy DMCC في دبي، ومؤسسًا لشركة Architaly Green Design في إيطاليا، أمضى مسيرته المهنية في تقاطع العمارة، والطاقة النظيفة، والتعليم، محوّلًا مبادئ الاستدامة إلى برامج تجارية ومدنية عملية عبر منطقة الخليج. وتشمل محفظته مشاريع تجارية وصناعية ومدنية يُعامَل فيها أداء الطاقة، والكربون المتضمَّن، والتصميم المتمحور حول الإنسان بوصفها تخصصات متكاملة لا يمكن الفصل بينها، وليست إضافات اختيارية.",
        "إلى جانب ممارسته التجارية، استثمر ديفيد باستمرار في التعليم من أجل الاستدامة. ابتكر برنامج «دبي للمدارس الشمسية» مع مبادرتَي «الفريق الأخضر» و«التنقّل الشمسي» المعتمدتَين في مدارس الإمارات، ويشغل منصب أستاذ فخري بكلية العمارة والتخطيط في جامعة Amity دبي، كما يشارك في الفريق القيادي لفرع Singularity University دبي، حيث يُرشد المؤسسين والطلاب في التقاطع العملي بين التكنولوجيا، والتصميم الحضري، والاقتصاد منخفض الكربون.",
      ],
      highlights_title: "أبرز المحطات المهنية",
      highlights: [
        "المؤسس والمدير التنفيذي، Architaly Green Energy DMCC، دبي، الإمارات",
        "مؤسس شركة Architaly Green Design، إيطاليا",
        "مؤسس منصة ESG Report Builder",
        "مبتكر برنامج «دبي للمدارس الشمسية» ومبادرتَي «الفريق الأخضر» و«التنقّل الشمسي» المعتمدتَين في مدارس الإمارات",
        "عضو الفريق القيادي لفرع Singularity University في دبي",
      ],
      recognition_title: "التكريم والتعليم",
      recognition: [
        "أستاذ فخري بكلية العمارة والتخطيط، جامعة Amity دبي",
        "تكريم في قمّة القادة العالميين الشباب برأس الخيمة تقديرًا لإسهاماته في التعليم من أجل الاستدامة",
        "مستشار ومحاضر منتظم في دور التكنولوجيا والاستدامة، أُدرج ضمن قائمة أبرز 50 شخصية في مجال الذكاء الاصطناعي في منطقة الخليج وفقًا لتصنيف Deep Knowledge Analytics",
      ],
      tags: ["العمارة الخضراء", "الطاقة المتجددة", "التعليم البيئي", "استراتيجية ESG"],
    },
  },
  {
    id: "ali",
    photo: "/founders/ali.jpg",
    linkedin: "https://www.linkedin.com/in/ali-mecirdi-6045a82",
    accent: "#01AF9B",
    previewScale: 1.241,
    previewTranslateY: 6.66,
    en: {
      name: "Ali Mecirdi",
      role: "Co-Founder",
      location: "United Arab Emirates",
      quote: "Digital transformation is not a technology exercise. It is a discipline of rollout, execution and integrated customer service.",
      overview: [
        "Ali Mecirdi is a serial entrepreneur, founder, co-founder, partnerships and business development leader, and Satellite VSAT engineer by technical background, with more than two decades of international experience at the intersection of satellite communications, telecoms, oil and gas, digital infrastructure, enterprise technology and managed services. As Founder of Omni Enterprise Global and Satmax Global, and Co-Founder at Jet Capsule and ESG Report Builder, he connects industries, markets and customers through practical innovation, future-ready digital solutions and strategic partnerships across the EMEA, Asia and South America regions.",
        "Through Omni Enterprise Global, Ali cultivated an ecosystem of innovation and B2B collaboration by forging alliances with pioneers in cutting-edge technologies. His mission reflects his entrepreneurial approach: to build trusted global partnerships and bridge the gap between businesses and customers through digital and telecommunications solutions that create measurable value across diverse industries worldwide.",
        "Throughout his career, Ali has built a strong reputation for opening new markets, developing high-value partnerships and positioning technology-led satellite connectivity solutions for oil and gas, telecoms, enterprise, government, energy, maritime, aviation and media and broadcast clients.",
      ],
      highlights_title: "Career Highlights",
      highlights: [
        "Founder, Omni Enterprise Global and Satmax Global",
        "Co-Founder, Jet Capsule",
        "Co-Founder, ESG Report Builder",
        "Senior global and regional leadership roles across EMEA, South America and Asia with Schlumberger, Harris CapRock, Space42 and Arabsat",
        "Delivered mobile satellite VSAT & broadband for Schlumberger Digital Oilfield Services across the Middle East and Africa",
      ],
      recognition_title: "Recognition & Education",
      recognition: [
        "Bachelor's degree in Telecommunications, Institut National des Télécommunications et des Technologies de l'Information et de la Communication (ITO Oran), Algeria",
        "Multilingual in English, Arabic and French",
      ],
      tags: ["Enterprise IT", "Telecommunications", "Digital Transformation", "Managed Services"],
    },
    ar: {
      name: "علي مسيردي",
      role: "شريك مؤسس",
      location: "الإمارات العربية المتحدة",
      quote: "التحوّل الرقمي ليس تمرينًا تقنيًا. بل هو انضباط في التنفيذ والنشر وخدمة العملاء المتكاملة.",
      overview: [
        "علي مسيردي رائد أعمال متسلسل، ومؤسس وشريك مؤسس، وقائد في تطوير الأعمال والشراكات، ومهندس اتصالات ساتلية (VSAT) من حيث التكوين التقني، بخبرة دولية تتجاوز عقدين في تقاطع الاتصالات الساتلية، والاتصالات، والنفط والغاز، والبنية الرقمية، وتكنولوجيا المؤسسات، والخدمات المُدارة. بصفته مؤسس Omni Enterprise Global وSatmax Global، وشريكًا مؤسسًا في Jet Capsule وESG Report Builder، يربط بين القطاعات والأسواق والعملاء عبر الابتكار العملي والحلول الرقمية المستقبلية والشراكات الاستراتيجية في مناطق أوروبا والشرق الأوسط وأفريقيا وآسيا وأمريكا الجنوبية.",
        "من خلال Omni Enterprise Global، رعى علي منظومة من الابتكار والتعاون بين الشركات عبر بناء تحالفات مع رواد التقنيات المتقدمة. تعكس مهمته نهجه الريادي: بناء شراكات عالمية موثوقة، وسدّ الفجوة بين الشركات والعملاء عبر حلول رقمية وتقنيات اتصالات تخلق قيمة قابلة للقياس عبر قطاعات متعددة حول العالم.",
        "على مدار مسيرته، بنى علي سمعة قوية في فتح أسواق جديدة، وتطوير شراكات عالية القيمة، وتموضع حلول الاتصال الساتلي القائمة على التكنولوجيا لصالح عملاء النفط والغاز، والاتصالات، والمؤسسات، والحكومات، والطاقة، والقطاع البحري، والطيران، والإعلام والبث.",
      ],
      highlights_title: "أبرز المحطات المهنية",
      highlights: [
        "مؤسس شركتَي Omni Enterprise Global وSatmax Global",
        "شريك مؤسس في Jet Capsule",
        "شريك مؤسس في ESG Report Builder",
        "مناصب قيادية عالمية وإقليمية عبر EMEA وأمريكا الجنوبية وآسيا في Schlumberger وHarris CapRock وSpace42 وArabsat",
        "قاد تقديم حلول VSAT والاتصال الساتلي عريض النطاق لخدمات Schlumberger Digital Oilfield Services في الشرق الأوسط وأفريقيا",
      ],
      recognition_title: "التكريم والتعليم",
      recognition: [
        "بكالوريوس في الاتصالات، المعهد الوطني للاتصالات وتكنولوجيا المعلومات (ITO وهران)، الجزائر",
        "يتقن الإنجليزية والعربية والفرنسية",
      ],
      tags: ["تقنية المعلومات المؤسسية", "الاتصالات", "التحول الرقمي", "الخدمات المُدارة"],
    },
  },
  {
    id: "jallel",
    photo: "/founders/jallel.jpg",
    linkedin: "https://www.linkedin.com/in/jallel-bendjaballah-3293006",
    accent: "#01AF9B",
    previewScale: 1.539,
    previewTranslateY: 0.39,
    en: {
      name: "Jallel Bendjaballah",
      role: "Co-Founder",
      location: "Kingdom of Saudi Arabia",
      quote: "Thirty years across the oil and gas industry, built on technical depth, operational excellence and an enduring commitment to health, safety and environmental standards.",
      overview: [
        "Jallel is a seasoned energy-industry executive with more than three decades of international leadership experience, recognised for delivering measurable impact in operations, business development, service quality, organisational transformation, and enterprise QHSE governance. As Group Vice President of Quality, Health, Safety & Environment (QHSE) at a major GCC oil-and-gas multinational enterprise, he drives the company's global agenda for quality, safety, health, environmental stewardship, risk management, and operational excellence, embedding performance discipline and sustainability across a diverse international footprint.",
        "His leadership ensured seamless alignment of systems, governance frameworks and organisational culture, while safeguarding business continuity and operational performance, strengthening regional presence and positioning the company for sustained growth.",
        "Prior to serving this major GCC oil-and-gas multinational enterprise, Jallel built a distinguished 21-year career at SLB (Schlumberger), advancing through leadership roles spanning wireline operations, service quality, integrated project management, marketing, human resources and business leadership. He led complex Integrated Project Management (IPM) and turnkey (LSTK) programmes, delivered service-quality transformation initiatives that elevated client trust across multiple geographies, and championed multicultural talent development.",
        "He began his career in the late 1990s as a Designer Engineer with Liebherr/ENMTP, and holds a State Engineering Degree (Ingénieur d'État) in Mechanical Engineering from Oum El Bouaghi University and a Maîtrise de Technologie from Constantine University, Algeria.",
      ],
      highlights_title: "Career Highlights",
      highlights: [
        "Group Vice President, Quality, Health, Safety & Environment (QHSE), at a major GCC oil-and-gas multinational enterprise",
        "21-year career at SLB (Schlumberger) across wireline, service quality, IPM, LSTK, marketing and HR",
        "Delivered complex Integrated Project Management (IPM) and turnkey (LSTK) programmes with measurable efficiency gains",
        "Started career as Designer Engineer at Liebherr/ENMTP in the late 1990s",
      ],
      recognition_title: "Recognition & Education",
      recognition: [
        "State Engineering Degree (Ingénieur d'État), Mechanical Engineering, Oum El Bouaghi University, Algeria",
        "Maîtrise de Technologie, Constantine University, Algeria",
        "Recognised for strategic vision, transformational leadership and operational excellence across the global energy sector",
      ],
      tags: ["QHSE Governance", "Oil & Gas Operations", "Operational Excellence", "Service Quality"],
    },
    ar: {
      name: "جلال بن جاب الله",
      role: "شريك مؤسس",
      location: "المملكة العربية السعودية",
      quote: "ثلاثون عامًا في قطاع النفط والغاز، مبنية على العمق التقني والتميّز التشغيلي والالتزام الدائم بمعايير الصحة والسلامة والبيئة.",
      overview: [
        "جلال تنفيذي مخضرم في قطاع الطاقة يمتلك أكثر من ثلاثة عقود من الخبرة القيادية الدولية، ومعروف بتحقيق أثر ملموس في العمليات، وتطوير الأعمال، وجودة الخدمة، والتحوّل المؤسسي، وحوكمة الجودة والصحة والسلامة والبيئة (QHSE) على مستوى المؤسسة. بصفته نائب الرئيس المجموعي لشؤون الجودة والصحة والسلامة والبيئة (QHSE) في مؤسسة كبرى متعدّدة الجنسيات للنفط والغاز في دول مجلس التعاون الخليجي، يقود جدول أعمال الشركة العالمي للجودة والسلامة والصحة والاستدامة البيئية وإدارة المخاطر والتميّز التشغيلي، مرسّخًا انضباط الأداء والاستدامة عبر بصمة دولية متنوّعة.",
        "ضمنت قيادته توحيد الأنظمة وأُطر الحوكمة والثقافة المؤسسية بسلاسة، مع الحفاظ على استمرارية الأعمال والأداء التشغيلي، معزّزًا الحضور الإقليمي وممهّدًا لنموّ مستدام للشركة.",
        "قبل انضمامه إلى هذه المؤسسة الكبرى متعدّدة الجنسيات للنفط والغاز في دول مجلس التعاون الخليجي، بنى جلال مسيرة متميّزة على مدى 21 عامًا في SLB (Schlumberger)، تدرّج فيها عبر مناصب قيادية تشمل عمليات Wireline، وجودة الخدمة، وإدارة المشاريع المتكاملة، والتسويق، والموارد البشرية، وقيادة الأعمال. قاد برامج إدارة مشاريع متكاملة (IPM) ومشاريع تسليم مفتاح (LSTK) معقّدة، ونفّذ مبادرات لتحوّل جودة الخدمة رسّخت ثقة العملاء عبر مناطق جغرافية متعدّدة، ورعى تطوير كوادر بشرية متعدّدة الثقافات.",
        "بدأ مسيرته في أواخر التسعينات مهندسًا مصمّمًا في Liebherr/ENMTP، وهو حاصل على شهادة مهندس دولة (Ingénieur d'État) في الهندسة الميكانيكية من جامعة أم البواقي وماجستير تكنولوجيا (Maîtrise de Technologie) من جامعة قسنطينة، الجزائر.",
      ],
      highlights_title: "أبرز المحطات المهنية",
      highlights: [
        "نائب رئيس المجموعة للجودة والصحة والسلامة والبيئة (QHSE) في مؤسسة كبرى متعدّدة الجنسيات للنفط والغاز في دول مجلس التعاون الخليجي",
        "مسيرة 21 عامًا في SLB (Schlumberger) عبر Wireline، وجودة الخدمة، وإدارة المشاريع المتكاملة، والتسويق، والموارد البشرية",
        "قاد برامج IPM وLSTK معقّدة حقّقت مكاسب ملموسة في الكفاءة",
        "بدأ مسيرته مهندسًا مصمّمًا في Liebherr/ENMTP في أواخر التسعينات",
      ],
      recognition_title: "التكريم والتعليم",
      recognition: [
        "شهادة مهندس دولة (Ingénieur d'État) في الهندسة الميكانيكية، جامعة أم البواقي، الجزائر",
        "ماجستير تكنولوجيا (Maîtrise de Technologie)، جامعة قسنطينة، الجزائر",
        "تكريم على الرؤية الاستراتيجية والقيادة التحوّلية والتميّز التشغيلي في قطاع الطاقة العالمي",
      ],
      tags: ["حوكمة QHSE", "عمليات النفط والغاز", "التميّز التشغيلي", "جودة الخدمة"],
    },
  },
  {
    id: "ade",
    photo: "/founders/ade.webp",
    linkedin: "https://www.linkedin.com/in/dammy72",
    accent: "#01AF9B",
    previewScale: 1.034,
    previewTranslateY: -5.14,
    en: {
      name: "Adedamola Oladeinde",
      role: "Co-Founder",
      location: "Canada",
      quote: "Few directors can interrogate a capital plan from the inside; capital-intensive companies deserve governance that scrutinises major spend, not only the paper it lands on.",
      overview: [
        "Adedamola Oladeinde is a professional engineer and corporate director with over two decades of experience leading major capital programmes across four continents, spanning oil and gas, mining, nuclear power, utilities and manufacturing. As Value Assurance Leader, Capital Program Efficiency at Saudi Aramco, he advises C-suite executives on the assurance and compliance of the company's major capital portfolio, embedding stage-gate discipline, independent assurance and cost-and-schedule control across multi-billion-dollar programmes.",
        "He has held senior project and portfolio leadership roles at Chevron, Rio Tinto, Vale, Qatargas, Bruce Power and Irving Oil. At Chevron Canada he directed facilities planning and execution for the Kaybob Duvernay programme, delivering 32 four-well appraisal pads on time and on budget with an installed cost of approximately US$700M. At Qatargas he provided project oversight for two offshore FEED and EPC developments with a combined installed cost of approximately $1.5B, and at Bruce Power he managed sustaining asset-management and major component replacement projects within Canada's largest nuclear facility, as part of a $13B capital programme.",
        "Adedamola is an active corporate director. He is Chair of the Board at Forensic Times, Board Advisory Member of Corkbrick Europe since October 2026, Director and Chair of the Nominating Committee at Mercy Ships Canada, Director at SafeLink Alberta and Venture Partner at Big Sky Capital. He owns board composition, succession and effectiveness, the mechanics of governance and not only its output, and continues to seek board and committee appointments with capital-intensive companies operating in regulated, high-consequence environments.",
      ],
      highlights_title: "Career Highlights",
      highlights: [
        "Value Assurance Leader, Capital Program Efficiency, Saudi Aramco",
        "Chair, Board of Directors, Forensic Times; Venture Partner, Big Sky Capital",
        "Board Advisory Member, Corkbrick Europe (since October 2026)",
        "Director & Chair, Nominating Committee, Mercy Ships Canada; Director, SafeLink Alberta, Director, Opera Atelier",
        "Senior project and portfolio leadership at Chevron, Rio Tinto, Vale, Qatargas, Bruce Power and Irving Oil",
        "Delivered projects across Canada, Saudi Arabia, Qatar, Madagascar, Malaysia and Nigeria",
      ],
      recognition_title: "Recognition & Education",
      recognition: [
        "ICD.D, Institute of Corporate Directors, Canada; Board Director Programme, Cambridge Judge Business School",
        "P.Eng. registrations in Alberta, British Columbia, Saskatchewan and Saudi Arabia; PfMP, PgMP, PMP, FIMC, M.CIoD, M.BDI",
        "DBA, PhD, MBA and executive education from leading global institutions: Oxford, Cambridge, and Harvard.",
      ],
      tags: ["Capital Program Assurance", "Corporate Governance", "Energy & Mining", "Board Leadership"],
    },
    ar: {
      name: "أديدامولا أولاديندي",
      role: "شريك مؤسس",
      location: "كندا",
      quote: "قلّة من أعضاء مجالس الإدارة يستطيعون تدقيق خطة رأسمالية من الداخل؛ والشركات كثيفة رأس المال تستحق حوكمة تفحص الإنفاق الكبير، لا مجرد الورق الذي يصل إلى المجلس.",
      overview: [
        "أديدامولا أولاديندي مهندس محترف وعضو مجلس إدارة يتمتّع بأكثر من عقدين من الخبرة في قيادة برامج رأسمالية كبرى في أربع قارات، تشمل قطاعات النفط والغاز والتعدين والطاقة النووية والمرافق والصناعة. بصفته قائدًا لضمان القيمة وكفاءة البرامج الرأسمالية في أرامكو السعودية، يُقدّم المشورة لكبار التنفيذيين حول ضمان الامتثال والمراجعة لمحفظة الشركة الرأسمالية الكبرى، ويرسّخ انضباط المراحل البوّابية، والضمان المستقل، وضبط التكلفة والجدول الزمني عبر برامج بمليارات الدولارات.",
        "شغل مناصب قيادية عليا في إدارة المشاريع والمحافظ لدى Chevron وRio Tinto وVale وQatargas وBruce Power وIrving Oil. في Chevron Canada قاد تخطيط وتنفيذ المنشآت لبرنامج Kaybob Duvernay، وسلّم 32 منصّة استكشاف من أربع آبار في الوقت المحدّد وضمن الميزانية بتكلفة تركيب تقارب 700 مليون دولار أمريكي. وفي Qatargas أشرف على مشروعَي FEED وEPC بحريَّين بتكلفة تركيب مجمّعة تقارب 1.5 مليار دولار، وفي Bruce Power أدار مشاريع إدارة الأصول واستبدال المكوّنات الرئيسية داخل أكبر منشأة نووية في كندا، ضمن برنامج رأسمالي بقيمة 13 مليار دولار.",
        "أديدامولا عضو مجلس إدارة فاعل. فهو رئيس مجلس إدارة Forensic Times، وعضو المجلس الاستشاري لشركة Corkbrick Europe منذ أكتوبر 2026، وعضو مجلس إدارة ورئيس لجنة الترشيحات في Mercy Ships Canada، وعضو مجلس إدارة SafeLink Alberta، وشريك المشاريع الرأسمالية في Big Sky Capital. يتولّى ملفات تكوين المجالس والخلافة والفاعلية، أي آليات الحوكمة لا مجرد مخرجاتها، ويواصل السعي لشغل مناصب في مجالس ولجان الشركات كثيفة رأس المال في البيئات المنظَّمة والحسّاسة.",
      ],
      highlights_title: "أبرز المحطات المهنية",
      highlights: [
        "قائد ضمان القيمة، كفاءة البرامج الرأسمالية، أرامكو السعودية",
        "رئيس مجلس إدارة Forensic Times، وشريك المشاريع الرأسمالية في Big Sky Capital",
        "عضو المجلس الاستشاري، Corkbrick Europe (منذ أكتوبر 2026)",
        "عضو مجلس إدارة ورئيس لجنة الترشيحات، Mercy Ships Canada؛ عضو مجلس إدارة، SafeLink Alberta؛ عضو مجلس إدارة، Opera Atelier",
        "قيادة عليا للمشاريع والمحافظ لدى Chevron وRio Tinto وVale وQatargas وBruce Power وIrving Oil",
        "سلّم مشاريع في كندا والمملكة العربية السعودية وقطر ومدغشقر وماليزيا ونيجيريا",
      ],
      recognition_title: "التكريم والتعليم",
      recognition: [
        "شهادة ICD.D من معهد أعضاء مجالس الإدارة الكندي؛ برنامج أعضاء مجالس الإدارة في كلية Cambridge Judge للأعمال",
        "مهندس محترف مسجَّل في ألبرتا، بريتيش كولومبيا، ساسكاتشوان، والمملكة العربية السعودية؛ PfMP وPgMP وPMP وFIMC وM.CIoD وM.BDI",
        "دكتوراه إدارة الأعمال (DBA)، ودكتوراه (PhD)، وماجستير إدارة الأعمال (MBA)، وتعليم تنفيذي من مؤسسات عالمية رائدة: أكسفورد، كامبريدج، وهارفارد.",
      ],
      tags: ["ضمان البرامج الرأسمالية", "حوكمة الشركات", "الطاقة والتعدين", "قيادة مجالس الإدارة"],
    },
  },
];

const COPY = {
  en: {
    eyebrow: "The people behind the platform",
    title: "Founders",
    intro: "ESG Report Builder is led by four partners who together bring more than a century of executive experience in sustainability, ESG, top management, enterprise technology, heavy-industry QHSE and capital-program governance. Rooted in the Gulf region and Canada, they represent the unique human factor behind every report the platform produces.",
    back: "Home",
    lang_toggle: "AR",
    linkedin_label: "LinkedIn",
  },
  ar: {
    eyebrow: "الأشخاص خلف المنصّة",
    title: "المؤسسون",
    intro: "تقود منصّة ESG Report Builder أربعة شركاء يجلبون معًا أكثر من قرن من الخبرة التنفيذية في مجالات الاستدامة، وESG، والإدارة العليا، وتكنولوجيا المؤسسات، وحوكمة QHSE في الصناعات الثقيلة، وحوكمة البرامج الرأسمالية. متجذّرون في منطقة الخليج وكندا، ويمثّلون العنصر الإنساني الفريد وراء كل تقرير تُنتجه المنصّة.",
    back: "الرئيسية",
    lang_toggle: "EN",
    linkedin_label: "لينكدإن",
  },
};

const FounderCard = ({ founder, index, isRtl, lang }) => {
  const isEven = index % 2 === 0;
  const f = founder[lang];
  return (
    <article
      className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10 lg:gap-14 items-start py-10 sm:py-16 lg:py-20 border-b border-white/[0.06] last:border-b-0 scroll-mt-28 sm:scroll-mt-32"
      data-testid={`founder-card-${founder.id}`}
    >
      {/* PHOTO */}
      <div className={`lg:col-span-5 ${isEven ? "" : "lg:order-2"}`}>
        <div className="relative max-w-[320px] sm:max-w-[380px] lg:max-w-[420px] mx-auto lg:mx-0">
          <div className="aspect-[4/5] w-full rounded-2xl overflow-hidden bg-[#0F1013] border-2 border-[#01AF9B]/20 shadow-2xl shadow-black/60">
            <img
              src={founder.photo}
              alt={f.name}
              className="w-full h-full object-cover object-center"
              data-testid={`founder-photo-${founder.id}`}
            />
          </div>
          {/* Country badge, anchored to one bottom corner */}
          <div
            className={`absolute -bottom-3 ${isEven ? "right-3 sm:right-4 lg:right-6" : "left-3 sm:left-4 lg:left-6"} text-white font-['Outfit'] text-[10px] sm:text-xs font-bold tracking-wider uppercase px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg shadow-lg`}
            style={{ backgroundColor: founder.accent }}
          >
            {f.location}
          </div>
          {/* LinkedIn pill, anchored to the OPPOSITE bottom corner, same vertical line as the country badge */}
          <a
            href={founder.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`founder-linkedin-${founder.id}`}
            className={`absolute -bottom-3 ${isEven ? "left-3 sm:left-4 lg:left-6" : "right-3 sm:right-4 lg:right-6"} inline-flex items-center gap-1.5 bg-[#0A66C2] hover:bg-[#0952A0] text-white font-['Outfit'] text-[10px] sm:text-xs font-bold tracking-wider uppercase px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg shadow-lg transition-colors`}
          >
            <Linkedin size={12} className="sm:w-3.5 sm:h-3.5" />
            {COPY[lang].linkedin_label}
          </a>
        </div>
      </div>

      {/* BIO */}
      <div className={`lg:col-span-7 ${isEven ? "" : "lg:order-1"}`} dir={isRtl ? "rtl" : "ltr"}>
        <div className="font-['Outfit'] text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase text-[#01AF9B] mb-2 sm:mb-3">
          {f.role}
        </div>
        <h3 className="font-['Outfit'] text-[26px] leading-[1.15] sm:text-4xl font-bold text-white mb-4 sm:mb-5 tracking-tight" data-testid={`founder-name-${founder.id}`}>
          {f.name}
        </h3>

        <blockquote className={`${isRtl ? "border-r-2 pr-4 sm:pr-5" : "border-l-2 pl-4 sm:pl-5"} border-[#01AF9B] mb-6 sm:mb-7 italic text-[15px] sm:text-lg text-slate-200 leading-relaxed`}>
          &ldquo;{f.quote}&rdquo;
        </blockquote>

        <div className="space-y-3.5 sm:space-y-4 mb-6 sm:mb-7">
          {f.overview.map((p, i) => (
            <p key={i} className={`text-[14.5px] sm:text-[15px] leading-relaxed text-slate-300 ${isRtl ? "text-right" : ""}`}>{p}</p>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-6">
          <div>
            <div className="font-['Outfit'] text-[11px] font-bold tracking-[0.25em] uppercase text-[#01AF9B] mb-3">
              {f.highlights_title}
            </div>
            <ul className="space-y-2.5">
              {f.highlights.map((h, i) => (
                <li key={i} className={`text-sm text-slate-300 leading-relaxed relative ${isRtl ? "pr-4 before:right-0" : "pl-4 before:left-0"} before:content-['•'] before:absolute before:text-[#01AF9B]`}>
                  {h}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-['Outfit'] text-[11px] font-bold tracking-[0.25em] uppercase text-[#01AF9B] mb-3">
              {f.recognition_title}
            </div>
            <ul className="space-y-2.5">
              {f.recognition.map((r, i) => (
                <li key={i} className={`text-sm text-slate-300 leading-relaxed relative ${isRtl ? "pr-4 before:right-0" : "pl-4 before:left-0"} before:content-['•'] before:absolute before:text-[#01AF9B]`}>
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-[#01AF9B]/5 border border-[#01AF9B]/15 rounded-lg p-5 mb-6" style={{ display: f.role_at_platform ? undefined : "none" }}>
          <div className="font-['Outfit'] text-[11px] font-bold tracking-[0.25em] uppercase text-[#01AF9B] mb-2">
            {f.role_at_platform_title}
          </div>
          <p className="text-[15px] leading-relaxed text-slate-200">{f.role_at_platform}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {f.tags.map((tag, i) => (
            <span
              key={i}
              className="font-['Outfit'] text-[10px] font-semibold tracking-[0.15em] uppercase text-[#01AF9B] border border-[#01AF9B]/40 rounded-full px-3.5 py-1.5"
              data-testid={`founder-tag-${founder.id}-${i}`}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
};

export default function FoundersPage() {
  const navigate = useNavigate();
  const { lang, isRtl, toggleLang } = useLang();
  const activeLang = lang === "ar" ? "ar" : "en";
  const copy = COPY[activeLang];
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#070709] text-white" dir={isRtl ? "rtl" : "ltr"}>
      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#070709] border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex-shrink-0"><Logo size="md" /></div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleLang}
              data-testid="founders-lang-toggle"
              className="flex items-center gap-1.5 font-['Outfit'] text-sm font-medium text-slate-400 hover:text-[#01AF9B] transition-colors border border-white/10 hover:border-[#01AF9B]/40 rounded-lg px-3 py-1.5"
            >
              <Globe size={14} />
              {copy.lang_toggle}
            </button>
            <button
              onClick={() => navigate("/")}
              data-testid="founders-back-home"
              className="flex items-center gap-2 font-['Outfit'] text-sm font-medium text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={16} className={isRtl ? "rotate-180" : ""} /> {copy.back}
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="pt-24 sm:pt-32 pb-10 sm:pb-14 px-5 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="font-['Outfit'] text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase text-[#01AF9B] mb-3 sm:mb-4">
            {copy.eyebrow}
          </div>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-5 sm:mb-6">
            {copy.title}
          </h1>
          <div className="w-14 sm:w-16 h-1 bg-[#01AF9B] mx-auto rounded-full mb-6 sm:mb-8"></div>
          <p className="text-base sm:text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed">
            {copy.intro}
          </p>
        </div>
      </section>

      {/* PREVIEW ROW — Jallel left, David & Ali centre, Adedamola right. Click a
          picture to smooth-scroll to the corresponding extended profile below.
          Equal spacing across the 4 avatars is guaranteed by a 4-col grid
          with a shared gap. */}
      <section className="pt-2 sm:pt-6 pb-14 sm:pb-24 px-5 sm:px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-4 gap-3 sm:gap-6 lg:gap-10">
          {[
            FOUNDERS.find(f => f.id === "jallel"),
            FOUNDERS.find(f => f.id === "david"),
            FOUNDERS.find(f => f.id === "ali"),
            FOUNDERS.find(f => f.id === "ade"),
          ].map((f) => (
            <button
              key={f.id}
              data-testid={`founder-preview-${f.id}`}
              onClick={() => {
                const el = document.querySelector(`[data-testid="founder-card-${f.id}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="group flex flex-col items-center gap-3 focus:outline-none"
            >
              <div className="w-20 h-20 sm:w-28 sm:h-28 lg:w-36 lg:h-36 rounded-full overflow-hidden border-2 border-white/10 group-hover:border-[#01AF9B] shadow-lg shadow-black/40 bg-white transition-all duration-300 group-hover:scale-[1.04]">
                <img
                  src={f.photo}
                  alt={f[activeLang].name}
                  className="w-full h-full object-cover"
                  style={{
                    objectPosition: "center top",
                    transform: `scale(${f.previewScale || 1}) translateY(${f.previewTranslateY || 0}%)`,
                    transformOrigin: "center top",
                  }}
                />
              </div>
              <div className="font-['Outfit'] text-[11px] sm:text-sm lg:text-base font-semibold text-slate-200 group-hover:text-white text-center tracking-tight">
                {f[activeLang].name}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* FOUNDERS LIST */}
      <section className="pb-16 sm:pb-24 px-5 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {FOUNDERS.map((founder, i) => (
            <FounderCard key={founder.id} founder={founder} index={i} isRtl={isRtl} lang={activeLang} />
          ))}
        </div>
      </section>
    </div>
  );
}
